import type { ContentType } from "~/models/Content/ContentType"
import type { SortField, SortOrder } from "~/models/Content/Sort"
import { formatForURL } from "./url"

export function buildProductKey(ibexaTitle: string) {
  // We only want the part after '/' and before the last '-' in the URL, ex. '/kunne/exphil-uio' -> 'exphil'

  const segments = ibexaTitle.split(" ")
  return formatForURL(
    segments.slice(0, segments.length - 1).join("-"),
  )
}

export interface IbexaQuery<T extends ContentType = ContentType> {
  limit?: number
  offset?: number
  role?: string
  text?: string
  fuzziness?: number
  boost?: Record<string, any>
  spellcheck?: boolean
  mainLocation?: boolean

  // Fields
  contentId?: number[]
  locationId?: number[]
  parentLocationId?: number[]
  contentType?: T[]
  subtree?: string[]
  label?: string[]
  activity?: string[]
  grade?: string[]
  access?: string[]
  template?: number[]

  // Sorting
  sortField?: SortField | Lowercase<SortField>
  sortOrder?: SortOrder | Lowercase<SortOrder>

  // Aggregation
  aggregationFields?: ("parentLocationId" | "template")[]
  aggregationPath?: string
  aggregationLimit?: number
}

export const IBEXA_HEADERS = {
  Accept: "application/vnd.ibexa.api.View+json; version=1.1",
  "Content-Type":
    "application/vnd.ibexa.api.ViewInput+json; version=1.1",
} as const

export const IBEXA_QUERY_DEFAULTS = {
  text: "*",
  aggregationLimit: 1000,
  role: "anon",
  limit: 100,
  offset: 0,
} as const satisfies IbexaQuery

export type IbexaQueryParams = Record<string, number | string | undefined | string[] | number[]>

export function buildIbexaQuery(
  query: IbexaQuery = IBEXA_QUERY_DEFAULTS,
): IbexaQueryParams {
  const params: IbexaQueryParams = {
    mainLocation: query.mainLocation ? 1 : 0,
    fullText: query.text || IBEXA_QUERY_DEFAULTS.text,
    role: query.role || IBEXA_QUERY_DEFAULTS.role,
    limit: query.limit ?? IBEXA_QUERY_DEFAULTS.limit,
    offset: query.offset ?? IBEXA_QUERY_DEFAULTS.offset,
    fuzziness: query.fuzziness,
    sortField: query.sortField?.toLowerCase(),
    aggregationPath: query.aggregationPath,
  }

  if (query.spellcheck) {
    params.spellcheck = 1
  }

  if (query.sortOrder) {
    const order = query.sortOrder.toLowerCase()
    params.sortOrder = order === "asc"
      ? "ascending"
      : order === "desc"
        ? "descending"
        : order
  }

  if (query.aggregationFields?.length) {
    params.aggregationLimit = query.aggregationLimit ?? 1000
  }

  if (query.boost) {
    for (const key in query.boost) {
      const value = query.boost[key]
      if (value) {
        params[`boost[${key}]`] = value
      }
    }
  }

  function addArrayParams(key: keyof IbexaQuery) {
    const raw = query[key]
    if (Array.isArray(raw) && raw.length > 0) {
      params[`${key}[]`] = raw
    }
  }

  addArrayParams("contentType")
  addArrayParams("subtree")
  addArrayParams("label")
  addArrayParams("activity")
  addArrayParams("grade")
  addArrayParams("contentId")
  addArrayParams("parentLocationId")
  addArrayParams("locationId")
  addArrayParams("access")
  addArrayParams("template")
  addArrayParams("aggregationFields")

  return params
}
